import React, { useState, useEffect, useRef, useCallback } from 'react';
import RadioQuestion from './QuestionTypes/RadioQuestion';
import CheckboxQuestion from './QuestionTypes/CheckboxQuestion';
import FillBlankQuestion from './QuestionTypes/FillBlankQuestion';
import CodeQuestion from './QuestionTypes/CodeQuestion';
import QuizAgreement from './QuizAgreement';
import judge0Service from '../services/judge0Service';
// import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import QuizService from '../services/quizService';
import AppSettingsService from '../services/appSettingsService';
import userService from '../services/userService';
import QuizSecurity from '../utils/quizSecurity';
import toastService from '../services/toastService';
import { formatDateTime } from '../utils/timeUtils';
// import ThemeToggle from './ThemeToggle';
import './QuizEngine.css';
import './QuizSecurity.css';

const QuizEngine = React.memo(({ currentUser, onLogout }) => {
  const quizService = QuizService; // Create local reference
  
  // Sound functions for alerts
  const playWarningSound = () => {
  try {
    const audio = new Audio('/assets/warning.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  } catch (error) {
    console.log('Warning sound not available');
  }
};

const playCriticalSound = () => {
  try {
    const audio = new Audio('/assets/critical.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (error) {
    console.log('Critical sound not available');
  }
};

  // Handle auto-submit for security violations
  const handleAutoSubmit = () => {
    console.log('🚨 Auto-submit triggered by security violation');
    setAutoSubmitMessage('Quiz auto-submitted due to security violation.');
    setAutoSubmitReason('security');
    setShowAutoSubmitPopup(true);
  };
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  // Timer states
  const [quizEndAt, setQuizEndAt] = useState(null); // timestamp in ms
  const [quizTimeLeft, setQuizTimeLeft] = useState(0); // seconds
  const [questionEndAt, setQuestionEndAt] = useState(null); // timestamp ms or null
  const [questionTimeLeft, setQuestionTimeLeft] = useState(null); // seconds or null
  const [quizTotalSeconds, setQuizTotalSeconds] = useState(0); // total quiz duration
  
  // Refs for stability
  const timerIntervalRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);
  const autoNextLockRef = useRef(false);
  const quizDataRef = useRef(null);
  const sessionIdRef = useRef(null);
  const userIdRef = useRef(null);
  const answersRef = useRef({});
  const codeLanguagesRef = useRef({});
  const randomizedQuestionsRef = useRef([]);
  const currentQuestionRef = useRef(0);
  const lastSubmissionReasonRef = useRef('manual');
  const isSubmittingRef = useRef(false);
  const submissionAttemptedRef = useRef(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [isCalculatingScore, setIsCalculatingScore] = useState(false);
  const [randomizedQuestions, setRandomizedQuestions] = useState([]);
  const [securityWarning, setSecurityWarning] = useState('');
  const [warningSeverity, setWarningSeverity] = useState('info'); // 'info', 'warning', 'critical'
  const [violationCount, setViolationCount] = useState({ type: '', current: 0, max: 0 });
  const [showModal, setShowModal] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState('');
  const [codeLanguages, setCodeLanguages] = useState({}); // Store selected languages for code questions
  const [showAutoSubmitPopup, setShowAutoSubmitPopup] = useState(false);
  const [autoSubmitMessage, setAutoSubmitMessage] = useState('');
  const [autoSubmitReason, setAutoSubmitReason] = useState('');
  const [submissionReason, setSubmissionReason] = useState(''); // Track actual submission reason
  const [activeSession, setActiveSession] = useState(null);
  
  // Agreement states
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showAgreement, setShowAgreement] = useState(true);
  
  // User info state
  const [userInfo, setUserInfo] = useState(null);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(true);
  const [isQuizSubmitted, setIsQuizSubmitted] = useState(false);
  
  // const { theme, toggleTheme } = useTheme();
  
  const securityRef = useRef(null);
  const inputRefs = useRef([]);

  const getAnswersStorageKey = useCallback(() => {
    const userId = userIdRef.current;
    const quizId = quizDataRef.current?.id;
    if (!userId || !quizId) return null;
    return `quiz_answers_${userId}_${quizId}`;
  }, []);

  const persistAnswersSnapshot = useCallback((answersSnapshot, languagesSnapshot) => {
    const key = getAnswersStorageKey();
    if (!key) return;
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          answers: answersSnapshot,
          codeLanguages: languagesSnapshot,
          updatedAt: Date.now()
        })
      );
    } catch (error) {
      console.error('Error persisting answers state:', error);
    }
  }, [getAnswersStorageKey]);

  const clearPersistedAnswers = useCallback((quizId) => {
    const userId = userIdRef.current;
    if (!userId || !quizId) return;
    try {
      localStorage.removeItem(`quiz_answers_${userId}_${quizId}`);
    } catch (error) {
      console.error('Error clearing persisted answers state:', error);
    }
  }, []);

  const clearForeignPersistedAnswers = useCallback((currentUserId) => {
    if (!currentUserId) return;
    try {
      const keepPrefix = `quiz_answers_${currentUserId}_`;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key.startsWith('quiz_answers_') && !key.startsWith(keepPrefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Error clearing foreign persisted answers:', error);
    }
  }, []);

  // Load agreement state from localStorage on component mount
  useEffect(() => {
    // Always require agreement - don't load from localStorage
    // This ensures agreement is shown every time
    setHasAgreed(false);
    setShowAgreement(true);
    
    // Clear any existing agreement state
    try {
      localStorage.removeItem('quizAgreement');
    } catch (error) {
      console.error('Error clearing agreement state:', error);
    }
  }, []);

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        setIsLoadingUserInfo(true);
        const result = await userService.getCurrentUser();
        if (result.success) {
          userIdRef.current = result.data?.id || null;
          setUserInfo(result.data);
          const quizSubmitted = result.data.is_quiz_submitted || false;
          setIsQuizSubmitted(quizSubmitted);
          
          // Log violation status from API
          console.log('✅ User info loaded:', result.data);
          console.log('✅ Quiz submission status:', quizSubmitted);
          console.log('✅ Violation status from API:', {
            is_violated: result.data.is_violated,
            violate_reason: result.data.violate_reason
          });

          clearForeignPersistedAnswers(userIdRef.current);
          
          // Route guard: Redirect if quiz already submitted
          if (quizSubmitted && window.location.pathname.includes('/quiz')) {
            toastService.error('You already gave the test');
            // Redirect to dashboard or home
            window.location.href = '/dashboard';
          }
        } else {
          console.warn('⚠️ Failed to fetch user info:', result.message);
          // Don't show toast error for token expiration - it's expected behavior
          if (!result.message.includes('expired') && !result.message.includes('No authentication token')) {
            toastService.error('Failed to load user information');
          }
        }
      } catch (error) {
        console.error('❌ Error fetching user info:', error);
        toastService.error('Error loading user information');
      } finally {
        setIsLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Load quiz data and check availability on component mount
  useEffect(() => {
    const loadQuizAndCheckAvailability = async () => {
      if (!userInfo) return; // Wait for user info to be loaded
      
      try {
        setIsLoading(true);
        const result = await quizService.getUserQuiz();
        
        if (!result.success || !result.data) {
          console.log('No active quiz available');
          return;
        }
        
        const quiz = result.data;
        const status = getQuizStatus(quiz);
        
        // Store quiz data for UI display even if not available
        setQuizData(quiz);
        quizDataRef.current = quiz;
        
        console.log('Quiz status:', status, 'Start:', quiz.start_date, 'End:', quiz.end_date);

        const sessionResult = await quizService.getCurrentQuizSession();
        if (sessionResult.success && sessionResult.data) {
          const session = sessionResult.data;

          if (session.quiz_status && session.quiz_status !== 'in_progress') {
            handleSubmittedSession(session);
            return;
          }

          if (Number(session.quizId) === Number(quiz.id)) {
            setHasAgreed(true);
            setShowAgreement(false);
            const resumeResult = await quizService.startQuizSession();
            await initializeQuizAttempt(
              quiz,
              resumeResult.success && resumeResult.data ? resumeResult.data : session,
              true
            );
            return;
          }
        }
        
      } catch (error) {
        console.error('Error loading quiz:', error);
        toastService.error('Failed to load quiz');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadQuizAndCheckAvailability();
  }, [userInfo]);

  // Single stable timer interval for both global and question timers
  useEffect(() => {
    if (quizStarted && !showResults && !quizSubmitted) {
      // Clear existing interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }

      timerIntervalRef.current = setInterval(() => {
        const now = Date.now();

        // Check server-backed session expiry during quiz execution
        if (quizEndAt) {
          const remaining = Math.max(0, Math.ceil((quizEndAt - now) / 1000));
          setQuizTimeLeft(remaining);

          if (remaining <= 0) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            submitQuiz('time_expired');
            return;
          }
        } else if (quizDataRef.current) {
          const endDate = new Date(quizDataRef.current.end_date);
          if (now > endDate.getTime()) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            submitQuiz('time_window_expired');
            return;
          }
        }

        // Update question timer if active
        if (questionEndAt) {
          const questionRemaining = Math.max(0, Math.ceil((questionEndAt - now) / 1000));
          setQuestionTimeLeft(questionRemaining);

          // Auto-next if question timer ends
          if (questionRemaining === 0 && !autoNextLockRef.current) {
            autoNextLockRef.current = true;
            handleAutoNext();
          }
        }
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    }
  }, [quizStarted, showResults, quizSubmitted, questionEndAt, quizEndAt]);

  const getQuestionTimeLimitSeconds = (question) => {
    const raw = question?.timeLimit;
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  };

  const goToNextQuestion = (reason = 'manual_next') => {
    const qs = randomizedQuestionsRef.current?.length ? randomizedQuestionsRef.current : randomizedQuestions;
    const idx = currentQuestionRef.current;
    if (idx < (qs?.length || 0) - 1) {
      const nextIndex = idx + 1;
      currentQuestionRef.current = nextIndex;
      setCurrentQuestion(nextIndex);
      scheduleAutosave(null, 150);
      return;
    }

    // All questions completed
    scheduleAutosave(null, 150);
    setQuizTimeLeft(0);
  };

  // Reset question timer when question changes
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
    
    // Guard: Only initialize if quiz is started, questions are ready, and we have a valid current question
    if (!quizStarted || showResults || quizSubmitted || !randomizedQuestions || randomizedQuestions.length === 0) {
      console.log('🔄 Question timer init skipped - conditions not met:', {
        quizStarted,
        showResults,
        quizSubmitted,
        hasQuestions: !!randomizedQuestions,
        questionsLength: randomizedQuestions?.length || 0
      });
      return;
    }
    
    const currentQ = randomizedQuestions[currentQuestion];
    if (!currentQ) {
      console.log('🔄 Question timer init skipped - no current question at index:', currentQuestion);
      return;
    }
    
    const timeLimit = currentQ?.timeLimit;
    
    console.log('🔄 Question changed to:', currentQuestion, 'timeLimit:', timeLimit);
    
    // Reset auto-next lock
    autoNextLockRef.current = false;
    
    if (timeLimit && timeLimit > 0) {
      setQuestionEndAt(Date.now() + timeLimit * 1000);
      setQuestionTimeLeft(timeLimit);
      console.log('⏰ Question timer started:', timeLimit, 'seconds');
    } else {
      setQuestionEndAt(null);
      setQuestionTimeLeft(null);
      console.log('⏰ Question timer disabled - no time limit');
    }
  }, [currentQuestion, quizStarted, showResults, quizSubmitted, randomizedQuestions]);

  // Track typing for input elements
  useEffect(() => {
    if (securityRef.current && inputRefs.current[randomizedQuestions[currentQuestion]?.id]) {
      const cleanup = securityRef.current.trackTyping(inputRefs.current[randomizedQuestions[currentQuestion]?.id]);
      return cleanup;
    }
  }, [currentQuestion, randomizedQuestions]);

  const handleAutoNext = () => {
    console.log('⏰ Auto-next triggered for question:', currentQuestion);
    toastService.info('Time\'s up — moving to next question');

    goToNextQuestion('question_timer');

    // If this was the last question, submit after marking remaining time as 0
    if (currentQuestionRef.current >= randomizedQuestionsRef.current.length - 1) {
      submitQuiz('question_timer');
    }
  };

  const handleTimeUp = () => {
    console.log('🕐 handleTimeUp triggered - Time is up!');
    console.log('Quiz submitted:', quizSubmitted);
    console.log('Quiz data exists:', !!quizData);
    
    if (!quizSubmitted && quizData) {
      console.log('🚀 Triggering timer auto-submit popup');
      setAutoSubmitMessage('Time is up! Quiz will be submitted automatically.');
      setAutoSubmitReason('timer');
      setShowAutoSubmitPopup(true);
    } else {
      console.log('⏸️ Timer auto-submit blocked - already submitted or no quiz data');
    }
  };

  const handleAgreement = (agreementData) => {
    console.log('📝 Agreement confirmed:', agreementData);
    setHasAgreed(true);
    setShowAgreement(false);
    
    // Don't save agreement state to localStorage - show every time after login
    
    // Start the quiz after agreement
    startQuiz();
  };

  const checkQuizAvailability = (quiz) => {
    if (!quiz) return false;
    
    const now = new Date();
    const startDate = new Date(quiz.start_date);
    const endDate = new Date(quiz.end_date);
    
    return now >= startDate && now <= endDate;
  };

  const getQuizStatus = (quiz) => {
    if (!quiz) return 'no-quiz';
    
    const now = new Date();
    const startDate = new Date(quiz.start_date);
    const endDate = new Date(quiz.end_date);
    
    if (now < startDate) {
      return 'not-started';
    } else if (now > endDate) {
      return 'expired';
    } else {
      return 'available';
    }
  };

  const mapApiQuestions = (quiz) => (quiz.questions || []).map(q => {
    console.log('ðŸ” Processing question from API:', q.id, q.question_text, 'time_limit:', q.time_limit);

    return {
      id: q.id,
      type: q.question_type,
      question: q.question_text,
      questionImageUrl: QuizService.getMediaUrl(q.question_image_url),
      options: q.options ? q.options.map(opt => ({
        text: opt.option_text,
        id: opt.id,
        imageUrl: QuizService.getMediaUrl(opt.image_url)
      })) : [],
      points: q.points,
      designation: q.designation,
      timeLimit: q.time_limit ?? null,
      language: q.language || null
    };
  });

  const orderQuestionsForSession = (questions, questionOrder = []) => {
    if (!Array.isArray(questionOrder) || questionOrder.length === 0) {
      return QuizSecurity.randomizeQuestions(questions);
    }

    const byId = new Map(questions.map((question) => [Number(question.id), question]));
    const ordered = questionOrder.map((id) => byId.get(Number(id))).filter(Boolean);
    const orderedIds = new Set(ordered.map((question) => Number(question.id)));
    const remaining = questions.filter((question) => !orderedIds.has(Number(question.id)));
    return [...ordered, ...remaining];
  };

  const restoreSessionAnswers = (session, questions) => {
    const nextAnswers = {};
    const nextLanguages = {};
    const byId = new Map(questions.map((question) => [Number(question.id), question]));

    (session?.answers || []).forEach((savedAnswer) => {
      const question = byId.get(Number(savedAnswer.questionId));
      if (!question) return;

      if (question.type === 'radio') {
        if (savedAnswer.optionIds?.length) {
          nextAnswers[question.id] = savedAnswer.optionIds[0];
        }
      } else if (question.type === 'checkbox') {
        nextAnswers[question.id] = savedAnswer.optionIds || [];
      } else {
        nextAnswers[question.id] = savedAnswer.answer_text || '';
      }

      if (savedAnswer.code_language) {
        nextLanguages[question.id] = savedAnswer.code_language;
      }
    });

    return { answers: nextAnswers, codeLanguages: nextLanguages };
  };

  const buildAnswerPayloadForQuestion = (question) => {
    const userAnswer = answersRef.current?.[question.id];
    const language = codeLanguagesRef.current?.[question.id] || question.language || '';

    if (question.type === 'radio') {
      const parsed = typeof userAnswer === 'string' ? Number(userAnswer) : userAnswer;
      const optionId = Number.isNaN(parsed) ? userAnswer : parsed;
      return {
        questionId: question.id,
        optionIds: optionId ? [optionId] : [],
        answer_text: '',
        code_language: ''
      };
    }

    if (question.type === 'checkbox') {
      return {
        questionId: question.id,
        optionIds: Array.isArray(userAnswer) ? userAnswer : [],
        answer_text: '',
        code_language: ''
      };
    }

    if (question.type === 'code') {
      return {
        questionId: question.id,
        optionIds: [],
        answer_text: typeof userAnswer === 'string' ? userAnswer : '',
        code_language: language
      };
    }

    return {
      questionId: question.id,
      optionIds: [],
      answer_text: typeof userAnswer === 'string' ? userAnswer : '',
      code_language: ''
    };
  };

  const handleSubmittedSession = (session) => {
    setActiveSession(session);
    sessionIdRef.current = session?.id || null;
    setIsQuizSubmitted(true);
    setQuizSubmitted(true);
    setShowAgreement(false);
    setHasAgreed(true);
    setQuizStarted(false);
    setShowResults(true);
    setSubmissionReason(session?.auto_submitted ? 'time_expired' : 'submitted');
    if (session?.violate_reason) {
      toastService.error(session.violate_reason);
    }
  };

  const scheduleAutosave = (questionId = null, delay = 700) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || !quizStarted || quizSubmitted) return;

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(async () => {
      const qs = randomizedQuestionsRef.current || [];
      const targetQuestion = questionId
        ? qs.find((question) => Number(question.id) === Number(questionId))
        : qs[currentQuestionRef.current];
      const answersToSave = targetQuestion ? [buildAnswerPayloadForQuestion(targetQuestion)] : [];

      const result = await quizService.saveSessionAnswers(
        sessionId,
        answersToSave,
        currentQuestionRef.current || 0
      );

      if (result.success && result.data) {
        setActiveSession(result.data);
        if (result.data.quiz_status && result.data.quiz_status !== 'in_progress') {
          handleSubmittedSession(result.data);
        }
      }
    }, delay);
  };

  const startSecurityMonitoring = async () => {
    const securitySettingsResult = await AppSettingsService.getSecuritySettings();
    const securitySettings = securitySettingsResult.success ? securitySettingsResult.data : undefined;

    if (!securityRef.current) {
      securityRef.current = new QuizSecurity(securitySettings);
    } else {
      securityRef.current.updateSettings(securitySettings);
    }

    securityRef.current.startMonitoring({
      onWarning: (message, type, current, max) => {
        setSecurityWarning(message);
        setWarningSeverity('warning');
        setViolationCount({ type, current, max });
        playWarningSound();

        if ('vibrate' in navigator) {
          navigator.vibrate(200);
        }

        setTimeout(() => {
          setSecurityWarning('');
          setWarningSeverity('info');
          setViolationCount({ type: '', current: 0, max: 0 });
        }, 8000);
      },
      onAutoSubmit: (message, type, current, max) => {
        console.log('ðŸš¨ Security auto-submit triggered!');
        console.log('Security message:', message);
        console.log('Quiz submitted:', quizSubmitted);

        setSecurityWarning(message);
        setWarningSeverity('critical');
        setViolationCount({ type, current, max });
        setShowModal(true);
        playCriticalSound();

        if ('vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }

        setAutoSubmitReason(message);
        submitQuiz('security');
      },
      onInfo: (message, type, current, max) => {
        setSecurityWarning(message);
        setWarningSeverity('info');
        setViolationCount({ type, current, max });

        setTimeout(() => {
          setSecurityWarning('');
          setWarningSeverity('info');
          setViolationCount({ type: '', current: 0, max: 0 });
        }, 3000);
      }
    }, securitySettings);
  };

  const initializeQuizAttempt = async (quiz, session, isResume = false) => {
    if (!session || session.quiz_status !== 'in_progress') {
      handleSubmittedSession(session);
      return;
    }

    setActiveSession(session);
    sessionIdRef.current = session.id;

    setQuizData(quiz);
    quizDataRef.current = quiz;
    setQuizTotalSeconds(quiz.total_time);

    const sessionEndAt = session.expires_at ? new Date(session.expires_at).getTime() : null;
    setQuizEndAt(sessionEndAt);
    setQuizTimeLeft(session.remaining_seconds ?? quiz.total_time);

    const processedQuestions = mapApiQuestions(quiz);
    const orderedQuestions = orderQuestionsForSession(processedQuestions, session.question_order);
    randomizedQuestionsRef.current = orderedQuestions;
    setRandomizedQuestions(orderedQuestions);

    const restored = restoreSessionAnswers(session, orderedQuestions);
    answersRef.current = restored.answers;
    codeLanguagesRef.current = restored.codeLanguages;
    setAnswers(restored.answers);
    setCodeLanguages(restored.codeLanguages);
    persistAnswersSnapshot(restored.answers, restored.codeLanguages);

    const nextIndex = Math.min(
      Math.max(Number(session.current_question_index || 0), 0),
      Math.max(orderedQuestions.length - 1, 0)
    );
    currentQuestionRef.current = nextIndex;
    setCurrentQuestion(nextIndex);

    await startSecurityMonitoring();

    setQuizStarted(true);
    toastService.success(isResume ? 'Quiz resumed successfully!' : 'Quiz started successfully!');
  };

  const startQuiz = async () => {
    if (isQuizSubmitted) {
      toastService.error('You already gave the test');
      return;
    }
    
    // Check if agreement is required
    if (!hasAgreed) {
      console.log('📋 Agreement required, showing agreement screen');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await quizService.getUserQuiz();
      
      if (!result.success) {
        toastService.error(result.message);
        return;
      }
      
      const quiz = result.data;
      
      if (!quiz) {
        toastService.error('No active quiz available');
        return;
      }
      
      if (!quiz.questions || quiz.questions.length === 0) {
        toastService.error('No questions available in this quiz');
        return;
      }
      
      if (!checkQuizAvailability(quiz)) {
        const status = getQuizStatus(quiz);
        if (status === 'not-started') {
          toastService.error(`Quiz will start at ${formatDateTime(quiz.start_date)}`);
        } else if (status === 'expired') {
          toastService.error(`Quiz has ended at ${formatDateTime(quiz.end_date)}`);
        } else {
          toastService.error('Quiz not available at this time.');
        }
        return;
      }

      const sessionResult = await quizService.startQuizSession();

      if (!sessionResult.success || !sessionResult.data) {
        toastService.error(sessionResult.message || 'Failed to start quiz session');
        return;
      }

      await initializeQuizAttempt(quiz, sessionResult.data, sessionResult.data.reconnect_count > 0);
      return;
      
      // Store quiz data and set up timers
      setQuizData(quiz);
      quizDataRef.current = quiz;
      setQuizTotalSeconds(quiz.total_time); // API returns seconds

      // Sequential total-time pool: start remaining total time from quiz.total_time
      setQuizEndAt(null);
      setQuizTimeLeft(quiz.total_time);

      try {
        const userId = userIdRef.current;
        const key = userId ? `quiz_answers_${userId}_${quiz.id}` : null;
        if (!key) throw new Error('No user id available for answer persistence');
        const persisted = localStorage.getItem(key);
        if (persisted) {
          const parsed = JSON.parse(persisted);
          const restoredAnswers = parsed?.answers && typeof parsed.answers === 'object' ? parsed.answers : {};
          const restoredLanguages = parsed?.codeLanguages && typeof parsed.codeLanguages === 'object' ? parsed.codeLanguages : {};
          answersRef.current = restoredAnswers;
          codeLanguagesRef.current = restoredLanguages;
          setAnswers(restoredAnswers);
          setCodeLanguages(restoredLanguages);
        }
      } catch (error) {
        console.error('Error restoring persisted answers:', error);
      }
      
      // Global timer persistence disabled for sequential question timing model
      
      // Initialize security
      const securitySettingsResult = await AppSettingsService.getSecuritySettings();
      const securitySettings = securitySettingsResult.success ? securitySettingsResult.data : undefined;

      if (!securityRef.current) {
        securityRef.current = new QuizSecurity(securitySettings);
      } else {
        securityRef.current.updateSettings(securitySettings);
      }

      // Process questions for frontend format with correct field mapping
      const processedQuestions = quiz.questions.map(q => {
        console.log('🔍 Processing question from API:', q.id, q.question_text, 'time_limit:', q.time_limit);
        
        return {
          id: q.id,
          type: q.question_type,
          question: q.question_text,
          questionImageUrl: QuizService.getMediaUrl(q.question_image_url),
          options: q.options ? q.options.map(opt => ({
            text: opt.option_text,
            id: opt.id,
            imageUrl: QuizService.getMediaUrl(opt.image_url)
          })) : [],
          points: q.points,
          designation: q.designation,
          timeLimit: q.time_limit ?? null, // Correct field mapping
          language: q.language || null
        };
      });
      
      console.log('🔍 Processed questions:', processedQuestions.length, 'questions');
      
      // Randomize questions
      const randomized = QuizSecurity.randomizeQuestions(processedQuestions);
      randomizedQuestionsRef.current = randomized;
      setRandomizedQuestions(randomized);
      
      // Initialize first question timer if we have questions
      if (randomized.length > 0) {
        const firstQuestion = randomized[0];
        const timeLimit = firstQuestion?.timeLimit;
        
        console.log('🎯 Initializing first question timer:', timeLimit);
        
        if (timeLimit && timeLimit > 0) {
          setQuestionEndAt(Date.now() + timeLimit * 1000);
          setQuestionTimeLeft(timeLimit);
          console.log('⏰ First question timer set to:', timeLimit, 'seconds');
        } else {
          setQuestionEndAt(null);
          setQuestionTimeLeft(null);
          console.log('⏰ First question has no time limit');
        }
      }
      
      // Start security monitoring
      securityRef.current.startMonitoring({
        onWarning: (message, type, current, max) => {
          setSecurityWarning(message);
          setWarningSeverity('warning');
          setViolationCount({ type, current, max });
          
          // Play warning sound
          playWarningSound();
          
          // Vibrate on mobile
          if ('vibrate' in navigator) {
            navigator.vibrate(200);
          }
          
          // Auto-hide after 8 seconds (longer for visibility)
          setTimeout(() => {
            setSecurityWarning('');
            setWarningSeverity('info');
            setViolationCount({ type: '', current: 0, max: 0 });
          }, 8000);
        },
        onAutoSubmit: (message, type, current, max) => {
          console.log('🚨 Security auto-submit triggered!');
          console.log('Security message:', message);
          console.log('Quiz submitted:', quizSubmitted);
          
          setSecurityWarning(message);
          setWarningSeverity('critical');
          setViolationCount({ type, current, max });
          setShowModal(true);
          
          // Play critical sound
          playCriticalSound();
          
          // Stronger vibration for critical
          if ('vibrate' in navigator) {
            navigator.vibrate([200, 100, 200]);
          }
          
          setAutoSubmitReason(message);
          // Actually submit the quiz, don't just show popup
          submitQuiz('security');
        },
        onInfo: (message, type, current, max) => {
          setSecurityWarning(message);
          setWarningSeverity('info');
          setViolationCount({ type, current, max });
          
          // Auto-hide info messages faster
          setTimeout(() => {
            setSecurityWarning('');
            setWarningSeverity('info');
            setViolationCount({ type: '', current: 0, max: 0 });
          }, 3000);
        }
      }, securitySettings);
      
      setQuizStarted(true);
      toastService.success('Quiz started successfully!');
      
    } catch (error) {
      console.error('Start quiz error:', error);
      toastService.error('Failed to start quiz. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    answersRef.current = {
      ...(answersRef.current || {}),
      [questionId]: answer
    };
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    persistAnswersSnapshot(answersRef.current, codeLanguagesRef.current);
    scheduleAutosave(questionId);
  };

  const handleLanguageChange = (questionId, language) => {
    codeLanguagesRef.current = {
      ...(codeLanguagesRef.current || {}),
      [questionId]: language
    };
    setCodeLanguages(prev => ({
      ...prev,
      [questionId]: language
    }));
    persistAnswersSnapshot(answersRef.current, codeLanguagesRef.current);
    scheduleAutosave(questionId);
  };

  const handleAutoSubmitConfirm = () => {
    console.log('✅ User confirmed auto-submit');
    console.log('Submit reason:', autoSubmitReason);
    setShowAutoSubmitPopup(false);
    
    // If this is a security violation, show disqualification message
    if (autoSubmitReason === 'security') {
      toastService.error('You have been disqualified due to rule violation.');
    }
    
    submitQuiz(autoSubmitReason);
  };

  const handleAutoSubmitCancel = () => {
    console.log('❌ User cancelled auto-submit');
    setShowAutoSubmitPopup(false);
    // Don't submit - user cancelled
  };

  const renderQuestion = () => {
    const question = randomizedQuestions[currentQuestion];
    if (!question) return null;
    
    console.log('🔍 Rendering question:', question);
    console.log('🔍 Question type:', question.type);
    console.log('🔍 Question ID:', question.id);
    
    const answer = answers[question.id];

    // Check for invalid question configuration
    if ((question.type === 'radio' || question.type === 'checkbox') && question.options.length < 2) {
      toastService.error('Invalid question configuration');
      return (
        <div className="question-error">
          <p>This question is not properly configured. Please contact support.</p>
        </div>
      );
    }

    switch (question.type) {
      case 'radio':
        return <RadioQuestion question={question} answer={answer} onAnswerChange={handleAnswerChange} inputRef={el => inputRefs.current[question.id] = el} />;
      case 'checkbox':
        return <CheckboxQuestion question={question} answer={answer} onAnswerChange={handleAnswerChange} inputRef={el => inputRefs.current[question.id] = el} />;
      case 'text':
        return <FillBlankQuestion question={question} answer={answer} onAnswerChange={handleAnswerChange} inputRef={el => inputRefs.current[question.id] = el} />;
      case 'code':
        return <CodeQuestion 
          question={question} 
          answer={answer} 
          onAnswerChange={handleAnswerChange} 
          onLanguageChange={handleLanguageChange}
          inputRef={el => inputRefs.current[question.id] = el} 
        />;
      default:
        return <div>Unknown question type: {question.type}</div>;
    }
  };

  const submitQuiz = async (reason = 'manual') => {
    const currentQuizData = quizDataRef.current || quizData;
    if (!currentQuizData) return;
    if (quizSubmitted) return;
    if (isSubmittingRef.current || submissionAttemptedRef.current) return;

    lastSubmissionReasonRef.current = reason;

    submissionAttemptedRef.current = true;
    isSubmittingRef.current = true;

    try {
      const qs = randomizedQuestionsRef.current || [];
      const idx = currentQuestionRef.current || 0;
      const currentQ = qs[idx];
      if (currentQ && (currentQ.type === 'text' || currentQ.type === 'code')) {
        const el = inputRefs.current?.[currentQ.id];
        const liveValue = el?.value;
        if (typeof liveValue === 'string') {
          answersRef.current = {
            ...(answersRef.current || {}),
            [currentQ.id]: liveValue
          };
          setAnswers(prev => ({
            ...prev,
            [currentQ.id]: liveValue
          }));
          persistAnswersSnapshot(answersRef.current, codeLanguagesRef.current);
        }
      }
    } catch (error) {
      console.error('Error flushing current input before submit:', error);
    }
    
    setIsCalculatingScore(true);
    setQuizSubmitted(true);
    setSubmissionReason(reason); // Track actual submission reason
    
    // Stop timer interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
      autosaveTimeoutRef.current = null;
    }
    
    // Stop security monitoring
    if (securityRef.current) {
      securityRef.current.stopMonitoring();
      
      // Log security report
      const report = securityRef.current.getViolationReport();
      console.log('Security Report:', report);
    }
    
    try {
      const bulkAnswers = buildBulkAnswersPayload(
        answersRef.current,
        codeLanguagesRef.current,
        randomizedQuestionsRef.current
      );

      console.log('🧾 Submit snapshot size:', bulkAnswers.length);
      console.log('🧾 Submit questionIds:', bulkAnswers.map(a => a.questionId));

      if (bulkAnswers.length === 0) {
        console.log('No answers were recorded; finalizing empty quiz session.');
      }
      
      // Validate code questions have languages
      const validationError = validateCodeQuestions(bulkAnswers);
      if (validationError) {
        setIsCalculatingScore(false);
        setQuizSubmitted(false);
        submissionAttemptedRef.current = false;
        isSubmittingRef.current = false;
        toastService.error(validationError);
        return;
      }
      
      console.log('Submitting bulk answers:', bulkAnswers);
      
      // Get violation reason from security instance (single source of truth)
      let violationReason = "No violation detected";
      
      if (securityRef.current) {
        violationReason = securityRef.current.getCurrentViolationReason();
      }
      
      console.log("Submitting quiz with violation_reason:", violationReason);
      
      // Submit bulk answers
      const result = await quizService.submitBulkAnswers(bulkAnswers, violationReason, sessionIdRef.current);
      
      if (result.success) {
        const awardedPoints = Number(result.data?.total_points);
        const hasBackendScore = Number.isFinite(awardedPoints);
        const scoreQuestions = randomizedQuestionsRef.current?.length
          ? randomizedQuestionsRef.current
          : randomizedQuestions;

        // Show success and lock UI
        toastService.success('Quiz submitted successfully!');
        setShowResults(true);
        setActiveSession(result.data || null);
        sessionIdRef.current = null;
        clearPersistedQuizState(); // Clear persisted state on successful submission
        clearPersistedAnswers(currentQuizData.id);
        answersRef.current = {};
        codeLanguagesRef.current = {};
        randomizedQuestionsRef.current = [];
        
        if (hasBackendScore) {
          setScore(awardedPoints);
        } else {
          const totalPoints = scoreQuestions.reduce((sum, q) => sum + q.points, 0);
          const answeredQuestions = bulkAnswers.filter(a =>
            a.answer_text || (a.optionIds && a.optionIds.length > 0)
          ).length;
          const estimatedScore = scoreQuestions.length
            ? Math.floor((answeredQuestions / scoreQuestions.length) * totalPoints)
            : 0;
          setScore(estimatedScore);
        }
        
      } else {
        // Handle different error types
        if (result.message?.includes('401') || result.message?.includes('Unauthorized')) {
          toastService.error('Session expired. Please login again.');
          // TODO: Redirect to login or call onLogout
        } else if (result.message?.includes('500') || result.message?.includes('server error')) {
          toastService.error('Submission failed. Please contact support.');
        } else {
          toastService.error('Submission failed. Please contact support.');
        }
      }
      
    } catch (error) {
      console.error('Submit quiz error:', error);
      toastService.error('Submission failed. Please contact support.');
    } finally {
      isSubmittingRef.current = false;
      setIsCalculatingScore(false);
    }
  };

  const buildBulkAnswersPayload = (answersSnapshot = {}, languagesSnapshot = {}, questionsSnapshot = null) => {
    console.log('🔧 Building bulk answers payload. codeLanguages(snapshot):', languagesSnapshot);

    const payload = [];

    const qs = Array.isArray(questionsSnapshot) ? questionsSnapshot : randomizedQuestions;
    for (const question of qs) {
      const userAnswer = answersSnapshot?.[question.id];

      if (question.type === 'radio') {
        if (userAnswer !== undefined && userAnswer !== null && String(userAnswer).trim() !== '') {
          const parsed = typeof userAnswer === 'string' ? Number(userAnswer) : userAnswer;
          const optionId = Number.isNaN(parsed) ? userAnswer : parsed;
          payload.push({
            questionId: question.id,
            optionIds: [optionId],
            answer_text: "",
            code_language: ""
          });
        }
      } else if (question.type === 'checkbox') {
        const optionIds = Array.isArray(userAnswer)
          ? userAnswer
              .map((v) => (typeof v === 'string' ? Number(v) : v))
              .map((v) => (Number.isNaN(v) ? v : v))
          : [];
        if (optionIds.length > 0) {
          payload.push({
            questionId: question.id,
            optionIds,
            answer_text: "",
            code_language: ""
          });
        }
      } else if (question.type === 'text') {
        const text = typeof userAnswer === 'string' ? userAnswer : '';
        if (text.trim()) {
          payload.push({
            questionId: question.id,
            optionIds: [],
            answer_text: text,
            code_language: ""
          });
        }
      } else if (question.type === 'code') {
        const code = answersSnapshot[question.id] || '';
        // Use selected language, fallback to question's predefined language, then to common default
        const selectedLanguage = languagesSnapshot?.[question.id];
        const defaultLanguage = question.language || 'c'; // Fallback to C language
        const finalLanguage = selectedLanguage || defaultLanguage;

        // Submit an entry for code questions when either code is provided OR a language is selected/defaulted.
        // This ensures backend gets `code_language` even if user didn't type code.
        if (code.trim() || finalLanguage) {
          console.log('🔧 Code question submission:', {
            questionId: question.id,
            selectedLanguage,
            questionLanguage: question.language,
            finalLanguage,
            hasCode: !!code.trim()
          });

          payload.push({
            questionId: question.id,
            optionIds: [],
            answer_text: code,
            code_language: finalLanguage
          });
        }
      }
    }

    return payload;
  };

  const validateCodeQuestions = (bulkAnswers) => {
    console.log('🔍 Validating code questions. codeLanguages:', codeLanguages);
    console.log('🔍 Bulk answers:', bulkAnswers);
    
    const codeQuestions = bulkAnswers.filter(a => {
      const question = randomizedQuestions.find(q => q.id === a.questionId);
      return question && question.type === 'code';
    });
    
    console.log('🔍 Code questions found:', codeQuestions);
    
    for (const codeAnswer of codeQuestions) {
      console.log('🔍 Checking code answer:', codeAnswer);
      if (codeAnswer.answer_text && codeAnswer.answer_text.trim() && !codeAnswer.code_language) {
        console.log('❌ Validation failed for question:', codeAnswer.questionId);
        return 'Please select a programming language for code questions.';
      }
    }
    
    console.log('✅ Code validation passed');
    return null;
  };

  const loadPersistedQuizState = () => {
    try {
      const state = localStorage.getItem('quizTimerState');
      return state ? JSON.parse(state) : null;
    } catch (error) {
      console.error('Error loading persisted quiz state:', error);
      return null;
    }
  };

  const savePersistedQuizState = (quizId, endAt) => {
    try {
      const state = {
        quizId,
        quizEndAt: endAt,
        savedAt: Date.now()
      };
      localStorage.setItem('quizTimerState', JSON.stringify(state));
    } catch (error) {
      console.error('Error saving persisted quiz state:', error);
    }
  };

  const clearPersistedQuizState = () => {
    try {
      localStorage.removeItem('quizTimerState');
      // Don't remove quizAgreement - we want to show agreement every time after login
    } catch (error) {
      console.error('Error clearing persisted quiz state:', error);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const restartQuiz = () => {
    // Clear timer interval
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Reset all states
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
    setQuizEndAt(null);
    setQuizTimeLeft(0);
    setQuestionEndAt(null);
    setQuestionTimeLeft(null);
    setQuizTotalSeconds(0);
    setQuizStarted(false);
    setQuizData(null);
    setQuizSubmitted(false);
    setRandomizedQuestions([]);
    setCodeLanguages({});
    clearPersistedQuizState();

    clearPersistedAnswers(quizDataRef.current?.id);
    answersRef.current = {};
    codeLanguagesRef.current = {};
    randomizedQuestionsRef.current = [];
    submissionAttemptedRef.current = false;
    isSubmittingRef.current = false;
    
    // Reset refs
    autoNextLockRef.current = false;
    
    // Stop security monitoring
    if (securityRef.current) {
      securityRef.current.stopMonitoring();
    }
    
    // Logout the user
    onLogout();
  };

  // Show agreement screen if not agreed
  if (showAgreement && !quizStarted) {
    return (
      <QuizAgreement
        onStartQuiz={handleAgreement}
        quizData={quizData}
        hasAgreed={hasAgreed}
        hasStarted={quizStarted}
        isSubmitted={quizSubmitted}
      />
    );
  }

if (!quizStarted) {
  const status = quizData ? getQuizStatus(quizData) : null;

  const statusConfig = (() => {
    if (!quizData) return { tone: "neutral", title: "No quiz available", desc: "There is no active quiz at the moment." };
    if (status === "not-started")
      return { tone: "info", title: "Not started yet", desc: `Starts at ${formatDateTime(quizData.start_date)}` };
    if (status === "expired")
      return { tone: "danger", title: "Expired", desc: `Ended at ${formatDateTime(quizData.end_date)}` };
    return { tone: "success", title: "", desc: "To the Exam portal" };
  })();

  const disabledReason =
    isQuizSubmitted ? "You already took this quiz." :
    isLoadingUserInfo ? "Loading your profile…" :
    !quizData ? "No active quiz available." :
    status !== "available" ? "Quiz is not available right now." :
    isLoading ? "Loading quiz…" :
    "";

  return (
    <div className="qwrap">
      <div className="qcontainer">
        {/* Header */}
        <header className="qheader">
          <div>
            <h1 className="qtitle">Welcome</h1>
            {/* <p className="qsub">Read the instructions and details before starting.</p> */}
          </div>
        </header>

        {/* Status */}
        <section className={`qstatus qstatus--${statusConfig.tone}`} aria-live="polite">
          <div className="qstatus__title">{statusConfig.title}</div>
          <div className="qstatus__desc">{statusConfig.desc}</div>
        </section>

        {/* User card */}
        <section className="qcard">
          <div className="qcard__title">Candidate</div>

          {isLoadingUserInfo ? (
            <div className="qskeleton">
              <div className="qskeleton__line w40" />
              <div className="qskeleton__line w70" />
              <div className="qskeleton__line w55" />
            </div>
          ) : userInfo ? (
            <>
              <div className="quser">
                <div className="qavatar" aria-hidden="true">
                  {userInfo?.name?.trim()?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="quser__main">
                  <div className="quser__name">{userInfo.name}</div>
                  <div className="qchips">
                    <span className="qchip">{userInfo.designation?.name || "No designation"}</span>
                    <span className="qchip qchip--soft">{userInfo.event?.name || "No event"}</span>
                  </div>
                </div>
              </div>

              <div className="qrows">
                <div className="qrow">
                  <span className="qrow__k">Email</span>
                  <span className="qrow__v">{userInfo.email || "—"}</span>
                </div>
                <div className="qrow">
                  <span className="qrow__k">Phone</span>
                  <span className="qrow__v">{userInfo.phone || "—"}</span>
                </div>
                {userInfo.is_violated && (
                  <div className="qrow" style={{borderTop: '1px solid #fef2f2', paddingTop: '8px', marginTop: '8px'}}>
                    <span className="qrow__k" style={{color: '#dc2626'}}>Violation</span>
                    <span className="qrow__v" style={{color: '#dc2626', fontSize: '13px'}}>
                      {userInfo.violate_reason || 'Security violation detected'}
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="qempty">
              <div className="qempty__title">Profile unavailable</div>
              <div className="qempty__desc">Please refresh the page or contact support.</div>
            </div>
          )}
        </section>

        {/* Submitted notice */}
        {isQuizSubmitted && (
          <section className="qcard qcard--danger">
            <div className="qcard__title">Quiz submitted</div>
            <p className="qmuted">You already gave the test. You can’t attempt again.</p>
          </section>
        )}

        {/* Quiz overview */}
        <section className="qcard">
          <div className="qcard__title">Quiz overview</div>

          {quizData ? (
            <>
              <div className="qoverview">
                <div>
                  <div className="qoverview__name">{quizData.title}</div>
                  {quizData.description && <p className="qmuted">{quizData.description}</p>}
                </div>
              </div>

              <div className="qmeta">
                <div className="qmeta__item">
                  <div className="qmeta__label">Total time</div>
                  <div className="qmeta__value">{formatTime(quizData.total_time)}</div>
                </div>
                <div className="qmeta__item">
                  <div className="qmeta__label">Questions</div>
                  <div className="qmeta__value">{quizData.questions?.length || 0}</div>
                </div>
                <div className="qmeta__item">
                  <div className="qmeta__label">Start</div>
                  <div className="qmeta__value">{formatDateTime(quizData.start_date)}</div>
                </div>
                <div className="qmeta__item">
                  <div className="qmeta__label">End</div>
                  <div className="qmeta__value">{formatDateTime(quizData.end_date)}</div>
                </div>
              </div>
            </>
          ) : (
            <p className="qmuted">No active quiz available at this time.</p>
          )}
        </section>

        {/* Rules */}
        <section className="qcard">
          <div className="qcard__title">Rules</div>
          <ul className="qrules">
            <li>Tab switching, AI tools, search engines, and external assistance are prohibited.</li>
            <li>The timer starts once you begin and cannot be paused.</li>
            <li>Stay on this page until you submit.</li>
          </ul>
        </section>

        {/* CTA */}
        <section className="qcta">
          <button
            onClick={startQuiz}
            className="qbtn"
            disabled={
              isLoading ||
              isLoadingUserInfo ||
              isQuizSubmitted ||
              !quizData ||
              status !== "available"
            }
            aria-disabled={
              isLoading ||
              isLoadingUserInfo ||
              isQuizSubmitted ||
              !quizData ||
              status !== "available"
            }
          >
            {isLoading ? "Loading…" :
              isQuizSubmitted ? "Already submitted" :
              !quizData ? "No quiz available" :
              status === "not-started" ? "Not started yet" :
              status === "expired" ? "Expired" :
              "Start quiz"
            }
          </button>

          {disabledReason && <div className="qhelper">{disabledReason}</div>}
        </section>
      </div>
      
      {/* Logout Button - Fixed Bottom Right */}
      <button 
        onClick={onLogout}
        className="quiz-logout-btn"
        title="Logout"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16,17 21,12 16,7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        Logout
      </button>
    </div>
  );
}

  if (showResults) {
    //const totalPoints = randomizedQuestions.reduce((sum, q) => sum + q.points, 0);
    //const percentage = totalPoints > 0 ? Math.round((score / totalPoints) * 100) : 0;
    //const passed = percentage >= 70; // Default passing score
    
    // Determine completion message based on submission reason
    const getCompletionMessage = () => {
      if (submissionReason === 'timer') {
        return {
          title: 'Time Up - Auto Submitted!',
          message: 'Quiz was automatically submitted due to time limit.',
          icon: '⏰',
          type: 'auto-submit'
        };
      } else if (submissionReason === 'time_window_expired') {
        return {
          title: 'Quiz Ended - Auto Submitted!',
          message: 'Quiz was automatically submitted because the end time was reached.',
          icon: '⏰',
          type: 'auto-submit'
        };
      } else if (submissionReason === 'question_timer') {
        return {
          title: 'Question Time Up - Auto Submitted!',
          message: 'Quiz was automatically submitted when last question time expired.',
          icon: '⏰',
          type: 'auto-submit'
        };
      } else if (submissionReason === 'security') {
        // Get specific violation details
        const violationReport = securityRef.current?.getViolationReport();
        let violationMessage = 'Quiz was submitted due to violations.';
        
        if (violationReport && violationReport.violations && violationReport.violations.length > 0) {
          const violationTypes = violationReport.violations.map(v => v.type || v.description).filter(Boolean);
          if (violationTypes.length > 0) {
            violationMessage = `Quiz was submitted due to: ${violationTypes.join(', ')}.`;
          }
        }
        
        return {
          title: 'Security Violation - Auto Submitted!',
          message: violationMessage,
          icon: '⚠️',
          type: 'auto-submit'
        };
      } else if (submissionReason === 'manual') {
        return {
          title: 'Thank You!',
          message: 'Quiz submitted successfully.',
          icon: '✅',
          type: 'manual-submit'
        };
      } else {
        return {
          title: 'Quiz Complete!',
          message: submissionReason ? `Quiz completed. ` : 'Quiz completed successfully.',
          icon: '🎯',
          type: 'default'
        };
      }
    };
    
    const completionInfo = getCompletionMessage();

    return (
      <div className="quiz-results">
        <div className={`completion-header ${completionInfo.type}`}>
          <h2>{completionInfo.icon} {completionInfo.title}</h2>
          <p className="completion-message">{completionInfo.message}</p>
        </div>
        
        
        <button onClick={restartQuiz} className="restart-btn">Thank You</button>
      </div>
    );
  }

  return (
    <div className="quiz-container">
      <div className="quiz-topbar">
  <div className="topbar-left">
    <div className="quiz-meta">
      <div className="quiz-title">{quizData?.title || 'Quiz'}</div>
      <div className="quiz-sub">
        Question <strong>{currentQuestion + 1}</strong> / {randomizedQuestions.length}
      </div>
      {/* <div className="quiz-total-time">
        Total Time: {formatTime(quizTotalSeconds)}
      </div> */}
    </div>
  </div>


  <div className="topbar-right">
    <div className={`timer-pill ${quizTimeLeft <= 30 ? "danger" : ""}`}>
      {/* ⏱ Time Left: {formatTime(quizTimeLeft)} */}
    </div>
    
    {questionTimeLeft !== null && (
      <div className={`question-timer-pill ${questionTimeLeft <= 10 ? "danger" : ""}`}>
        ⏰ Question: {formatTime(questionTimeLeft)}
      </div>
    )}

    {/* Theme toggle button removed - dark mode disabled */}
    {/* <button
      onClick={toggleTheme}
      className="icon-btn"
      aria-label="Toggle theme"
      title="Toggle theme"
      type="button"
    >
      {theme === "light" ? (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414 1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button> */}

  
  </div>


</div>

<div className="mini-progress">
  <div className="mini-progress-bar">
    <div
      className="mini-progress-fill"
      style={{ width: `${((currentQuestion + 1) / randomizedQuestions.length) * 100}%` }}
    />
  </div>
</div>


      {/* Security Warning Banner */}
      {securityWarning && (
        <>
          {/* Backdrop for warning/critical severity */}
          {(warningSeverity === 'warning' || warningSeverity === 'critical') && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: warningSeverity === 'critical' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none',
                zIndex: 9998,
                animation: 'fadeIn 0.3s ease-in'
              }}
            />
          )}
          
          {/* Main Warning Banner */}
          <div 
            className={`security-warning-banner security-warning-${warningSeverity}`}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: warningSeverity === 'info' ? '56px' : '72px',
              backgroundColor: warningSeverity === 'critical' ? '#dc2626' : warningSeverity === 'warning' ? '#f59e0b' : '#3b82f6',
              color: '#ffffff',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: warningSeverity === 'info' ? '18px' : '20px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderLeft: warningSeverity === 'critical' ? '6px solid #991b1b' : warningSeverity === 'warning' ? '6px solid #d97706' : '6px solid #1e40af',
              animation: `slideDown 0.4s ease-out ${warningSeverity === 'warning' || warningSeverity === 'critical' ? 'shake 0.5s ease-in-out' : ''}`,
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
            role="alert"
            aria-live="assertive"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', maxWidth: '1200px', padding: '0 20px' }}>
              <span style={{ fontSize: '24px' }}>
                {warningSeverity === 'critical' ? '🚨' : warningSeverity === 'warning' ? '⚠️' : 'ℹ️'}
              </span>
              
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', marginBottom: '2px' }}>
                  {warningSeverity === 'critical' ? 'SECURITY VIOLATION' : warningSeverity === 'warning' ? 'WARNING' : 'NOTICE'}
                </div>
                <div style={{ fontSize: warningSeverity === 'info' ? '16px' : '18px' }}>
                  {securityWarning}
                </div>
                {violationCount.type && (
                  <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '2px' }}>
                    {violationCount.type.replace(/([A-Z])/g, ' $1').toUpperCase()}: {violationCount.current}/{violationCount.max}
                  </div>
                )}
              </div>
              
              {/* Progress bar for warnings */}
              {warningSeverity === 'warning' && violationCount.max > 0 && (
                <div style={{ 
                  width: '120px', 
                  height: '4px', 
                  backgroundColor: 'rgba(255, 255, 255, 0.3)', 
                  borderRadius: '2px',
                  overflow: 'hidden',
                  marginLeft: '12px'
                }}>
                  <div style={{
                    width: `${(violationCount.current / violationCount.max) * 100}%`,
                    height: '100%',
                    backgroundColor: '#ffffff',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Critical Violation Modal */}
      {showModal && warningSeverity === 'critical' && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-in'
          }}
        >
          <div 
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.3)',
              border: '2px solid #dc2626',
              animation: 'scaleIn 0.3s ease-out'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚨</div>
              <h2 style={{ color: '#dc2626', marginBottom: '12px', fontSize: '24px' }}>
                Quiz Auto-Submitted
              </h2>
              <p style={{ color: '#374151', fontSize: '16px', lineHeight: '1.5', marginBottom: '20px' }}>
                {securityWarning}
              </p>
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <strong style={{ color: '#dc2626' }}>Consequence:</strong> Your quiz has been automatically submitted due to security violations.
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

        <div className="question-card">
          <div className="question-container">
            <div className="question-header">
              <div className="question-title">
              <pre style={{
                background: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'system-ui, sans-serif',
                fontSize: '14px',
                maxHeight: '200px',
                overflow: 'auto',
                width: '100%',
              }}>
                <span style={{ fontWeight: '800' }}>
                  {randomizedQuestions[currentQuestion]?.question}
                </span>
              </pre>
            </div>
              
              {/* Per-question timer display */}
              {/* {questionTimeLeft !== null ? (
                <div className="question-timer-display">
                  Question Time Left: <strong>{formatTime(questionTimeLeft)}</strong>
                </div>
              ) : (
                <div className="question-timer-display">
                  No time limit
                </div>
              )} */}
            </div>
            {renderQuestion()}
          </div>

        <div className="quiz-navigation">
          {/* <button 
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0 || quizSubmitted}
            className="nav-btn"
          >
            Previous
          </button> */}
          <button 
            onClick={() => goToNextQuestion('manual_next')}
            disabled={currentQuestion === randomizedQuestions.length - 1 || quizSubmitted}
            className="nav-btn"
          >
            Next
          </button>
          {currentQuestion === randomizedQuestions.length - 1 && (
            <button 
              onClick={submitQuiz} 
              disabled={isCalculatingScore || quizSubmitted} 
              className="submit-btn"
            >
              {isCalculatingScore ? 'Submitting...' : 'Submit Quiz'}
            </button>
          )}
        </div>
      </div>

      {/* Auto-Submit Popup */}
      {showAutoSubmitPopup && (
        <div className="popup-overlay">
          <div className="popup-dialog">
            <div className="popup-header">
              <h3>Auto-Submit Quiz</h3>
            </div>
            <div className="popup-body">
              <p>{autoSubmitMessage}</p>
            </div>
            <div className="popup-footer">
              <button 
                onClick={handleAutoSubmitConfirm}
                className="popup-btn popup-btn-primary"
              >
                OK
              </button>
              {/* <button 
                onClick={handleAutoSubmitCancel}
                className="popup-btn popup-btn-secondary"
              >
                Cancel
              </button> */}
            </div>
          </div>
        </div>
      )}
      
      {/* Logout Button - Fixed Bottom Right */}
      <button 
        onClick={onLogout}
        className="quiz-logout-btn"
        title="Logout"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16,17 21,12 16,7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        Logout
      </button>
    </div>
  );
});

export default QuizEngine;
